"use client";

import { getCountries } from "@/actions/dropdowns";
import { TCountry } from "@/actions/types";
import { useCallback, useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";

type RenderCountriesProps = {
  children: (countries: TCountry[]) => React.ReactNode;
};

export default function RenderCountries({ children }: RenderCountriesProps) {
  const [countries, countriesSet] = useState<TCountry[]>([]);
  const [isLoading, isLoadingSet] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);

  // Read from localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    setHasMounted(true);
    const stored = localStorage.getItem("countries");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          countriesSet(parsed);
          isLoadingSet(false);
        }
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  const getData = useCallback(async () => {
    try {
      isLoadingSet(true);
      const res = await getCountries();
      if (res.status === "success" && res.metaData) {
        countriesSet(res.metaData);
        // Save to localStorage for future use
        localStorage.setItem("countries", JSON.stringify(res.metaData));
      }
      isLoadingSet(false);
    } catch (err) {
      console.log(err);
      isLoadingSet(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if we don't have cached data
    if (hasMounted && countries.length === 0) {
      getData();
    }
  }, [hasMounted, countries.length, getData]);

  // Show skeleton during initial mount or when loading without data
  if (!hasMounted || (isLoading && countries.length === 0)) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-5 w-16" />
        <div className="h-4 w-full flex gap-x-2">
          <Skeleton className="h-full w-12" />
          <Skeleton className="h-full w-20" />
        </div>
      </div>
    );
  }

  return <>{children(countries)}</>;
}
